// Shared date formatters for MyPatria — every UI surface should call one of
// these instead of rendering raw ISO strings, microseconds, or timezone offsets.
//
// formatDate("2026-07-25")              → "Jul 25, 2026"
// formatDate("2026-07-25T12:28:04.123Z", { withTime: true }) → "Jul 25, 2026 · 12:28 AM"
// formatRelative(iso)                    → "Just now" / "3 minutes ago" / "Yesterday"

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  if (!s) return null;
  // Handle plain YYYY-MM-DD as local (avoid UTC shifting to previous day).
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

export function formatDate(value, opts = {}) {
  const d = toDate(value);
  if (!d) return opts.fallback || "—";
  const withTime = !!opts.withTime;
  const month = MONTHS[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  if (!withTime) return `${month} ${day}, ${year}`;
  let hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${month} ${day}, ${year} · ${hours}:${mins} ${ampm}`;
}

export function formatDateTime(value, opts = {}) {
  return formatDate(value, { ...opts, withTime: true });
}

export function formatRelative(value) {
  const d = toDate(value);
  if (!d) return "—";
  const now = Date.now();
  const diff = now - d.getTime();
  if (diff < 60 * 1000) return "Just now";
  if (diff < 60 * 60 * 1000) {
    const m = Math.floor(diff / 60000);
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (diff < 24 * 60 * 60 * 1000) {
    const h = Math.floor(diff / 3600000);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  if (diff < 48 * 60 * 60 * 1000) return "Yesterday";
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    const days = Math.floor(diff / 86400000);
    return `${days} days ago`;
  }
  return formatDate(d);
}

// Alias so imports read naturally in a UI ("last login: {humanDate(...)}").
export const humanDate = formatDate;
export const humanDateTime = formatDateTime;
